import { createContext, type ReactNode, useContext, useState } from "react";

type FilterValue = string;

interface FilterContextType {
  getFilter: (key: string, defaultValue: FilterValue) => FilterValue;
  setFilter: (key: string, value: FilterValue) => void;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const FilterProvider = ({ children }: { children: ReactNode }) => {
  const [filters, setFilters] = useState<Record<string, FilterValue>>(() => {
    const stored: Record<string, FilterValue> = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith("filter_")) {
        const filterKey = key.replace("filter_", "");
        const value = sessionStorage.getItem(key);
        if (value) {
          stored[filterKey] = value;
        }
      }
    }
    return stored;
  });

  const getFilter = (key: string, defaultValue: FilterValue): FilterValue => {
    return filters[key] ?? defaultValue;
  };

  const setFilter = (key: string, value: FilterValue) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    sessionStorage.setItem(`filter_${key}`, value);
  };

  return (
    <FilterContext.Provider value={{ getFilter, setFilter }}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilter = (key: string, defaultValue: FilterValue) => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilter must be used within a FilterProvider");
  }

  const value = context.getFilter(key, defaultValue);
  const setValue = (newValue: FilterValue) => context.setFilter(key, newValue);

  return [value, setValue] as const;
};
